import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { authService } from "../services/api";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validación de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setError("Por favor, ingresa tu correo electrónico");
      toast.error("Por favor, ingresa tu correo electrónico");
      return;
    }

    if (!emailRegex.test(email)) {
      setError("Por favor, ingresa un correo electrónico válido");
      toast.error("Por favor, ingresa un correo electrónico válido");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authService.forgotPassword(email);

      if (response.success) {
        setIsSuccess(true);
        toast.success("Correo enviado exitosamente", {
          duration: 5000,
        });
      }
    } catch (error) {
      setError(error.message || "Error al enviar el correo");
      toast.error(error.message || "Error al enviar el correo");
    } finally {
      setIsSubmitting(false);
    }
  };

  const backgroundPattern = `data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E`;

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: `url("${backgroundPattern}")` }}
        ></div>

        <div className="relative bg-white/95 backdrop-blur-xl p-8 rounded-3xl shadow-2xl w-full max-w-md border border-white/20">
          <div className="text-center">
            <div className="mx-auto h-20 w-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-xl">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>

            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-3">
              ¡Correo Enviado!
            </h1>

            <p className="text-gray-600 mb-6">
              Hemos enviado un enlace de recuperación a:
            </p>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <p className="text-purple-700 font-semibold break-all">{email}</p>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 text-left">
              <p className="text-sm text-blue-700">
                <strong>Importante:</strong> El enlace expirará en 1 hora por
                razones de seguridad.
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Revisa tu bandeja de entrada y haz clic en el enlace para
                restablecer tu contraseña.
              </p>

              <p className="text-xs text-gray-500">
                Si no recibes el correo en unos minutos, verifica tu carpeta de
                spam.
              </p>
            </div>

            <Link
              to="/login"
              className="mt-6 inline-flex items-center justify-center w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Volver al inicio de sesión
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div
        className="absolute inset-0 opacity-20"
        style={{ backgroundImage: `url("${backgroundPattern}")` }}
      ></div>

      <div className="relative bg-white/95 backdrop-blur-xl p-8 rounded-3xl shadow-2xl w-full max-w-md border border-white/20">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-20 w-20 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-xl">
            <Mail className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">
            ¿Olvidaste tu contraseña?
          </h1>
          <p className="text-gray-600">
            No te preocupes, te ayudaremos a recuperarla
          </p>
        </div>

        {/* Mensaje de error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Información */}
        <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
          <p className="text-sm text-blue-700">
            Ingresa tu correo electrónico y te enviaremos un enlace para
            restablecer tu contraseña.
          </p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Correo Electrónico
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-200 transition-all duration-200 outline-none bg-white/70"
                placeholder="correo@ejemplo.com"
                disabled={isSubmitting}
                autoComplete="email"
                autoFocus
              />
            </div>
          </div>

          {/* Botón Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center text-lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin h-6 w-6 mr-3" />
                Enviando...
              </>
            ) : (
              <>
                <Mail className="h-6 w-6 mr-3" />
                Enviar enlace de recuperación
              </>
            )}
          </button>
        </form>

        {/* Volver al login */}
        <div className="mt-6">
          <Link
            to="/login"
            className="flex items-center justify-center text-purple-600 hover:text-purple-700 font-medium transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Volver al inicio de sesión
          </Link>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Sistema de Gestión Logística • {process.env.REACT_APP_BRAND_NAME || "Synkro Fleet"}
          </p>
          <div className="mt-3 flex justify-center space-x-2">
            <div className="h-2 w-2 rounded-full bg-purple-400"></div>
            <div className="h-2 w-2 rounded-full bg-purple-300"></div>
            <div className="h-2 w-2 rounded-full bg-purple-200"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
